import React, { useState, useEffect, useRef } from "react";
import { Room, RoomEvent, Track } from "livekit-client";

const PracticalWEBRTCMonitoring = React.forwardRef(({ trainee }, ref) => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authType, setAuthType] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  const roomRef = useRef(null);
  const participantsMapRef = useRef(new Map());
  const [roomName, setRoomName] = useState(null);
  const recordingStartedRef = useRef(false);

  useEffect(() => {
    try {
      const persistedAuth = window.localStorage?.getItem("persist:tagnet-auth");
      if (persistedAuth) {
        const parsed = JSON.parse(persistedAuth);
        const userData = parsed.user ? JSON.parse(parsed.user) : null;
        const token = parsed.authToken ? JSON.parse(parsed.authToken) : null;
        const userType = parsed.userType ? JSON.parse(parsed.userType) : null;

        setAuthType(userType);
        setUser(userData);
        setAuthToken(token);

        if (token) {
          if (userType === "trainee" && userData?.BatchId) {
            getWebRTCToken(token, { batchCode: userData.BatchId });
          } else {
            getWebRTCToken(token, { username: trainee.Username });
          }
        }
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
      setLoading(false);
    }
  }, [trainee]);

  const getWebRTCToken = async (token, payload) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: payload ? JSON.stringify(payload) : JSON.stringify({}),
      };

      const response = await fetch(
        `${baseUrl}/api/get_practical_rtc_token_for_web`,
        requestOptions
      );
      const result = await response.json();

      if (result.status) {
        await connectToRoom(result);
      }
    } catch (error) {
      console.error("Error fetching token:", error);
      setLoading(false);
    }
  };

  const decodeIdentity = (identityStr) => {
    try {
      return JSON.parse(atob(identityStr));
    } catch (err) {
      return { username: identityStr };
    }
  };

  const updateParticipants = () => {
    const participantsArray = Array.from(participantsMapRef.current.values());
    setParticipants(participantsArray);
  };

  const addParticipant = (participant, isLocal = false) => {
    const identity = decodeIdentity(participant.identity);
    const uniqueKey = identity.username || participant.identity;

    if (participantsMapRef.current.has(uniqueKey)) {
      return;
    }

    if (authType === "assessor") {
      if (identity?.username === "admin" || identity?.role === "assessor") {
        console.log(`Filtering out participant:`, identity.username);
        return;
      }
    } else {
      if (identity?.username === "admin" || identity?.role === "trainee") {
        console.log(`Filtering out participant:`, identity.username);
        return;
      }
    }

    const participantObj = {
      id: participant.sid,
      identity: participant.identity,
      uniqueKey: uniqueKey,
      participant: participant,
      decodedIdentity: identity,
      isLocal: isLocal,
    };

    participantsMapRef.current.set(uniqueKey, participantObj);
    updateParticipants();
  };

  const removeParticipant = (participant) => {
    const identity = decodeIdentity(participant.identity);
    const uniqueKey = identity.username || participant.identity;

    participantsMapRef.current.delete(uniqueKey);
    updateParticipants();
  };

  const connectToRoom = async (data) => {
    try {
      const serverUrl = "https://webrtc.tagindia.co.in";
      const { room: roomName, token } = data;
      setRoomName(roomName);
      const room = new Room({
        autoSubscribe: true,
        adaptiveStream: true,
        dynacast: true,
      });

      await room.connect(serverUrl, token);
      roomRef.current = room;

      const localParticipant = room.localParticipant;

      try {
        await localParticipant.setCameraEnabled(true);
        await localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        console.error("Failed to enable camera/microphone:", err);
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      addParticipant(localParticipant, true);

      for (const participant of room.remoteParticipants.values()) {
        addParticipant(participant, false);
        attachParticipantEvents(participant);
      }

      room.on(RoomEvent.ParticipantConnected, (participant) => {
        addParticipant(participant, false);
        attachParticipantEvents(participant);

        console.log("participant", participant);

        if (!recordingStartedRef.current) {
          startRecording(roomName);
          recordingStartedRef.current = true;
        }
      });

      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        removeParticipant(participant);
      });

      setConnected(true);
      setLoading(false);
    } catch (error) {
      console.error("Failed to connect to room:", error);
      setLoading(false);
    }
  };

  const attachParticipantEvents = (participant) => {
    participant.on(RoomEvent.TrackSubscribed, () => updateParticipants());
    participant.on(RoomEvent.TrackUnsubscribed, () => updateParticipants());
    participant.on(RoomEvent.TrackMuted, () => updateParticipants());
    participant.on(RoomEvent.TrackUnmuted, () => updateParticipants());
  };

  useEffect(() => {
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, []);

  const startRecording = async (room) => {
    try {
      const response = await fetch(`${baseUrl}/api/startRecording`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          room: room,
        }),
      });

      const data = await response.json();
      console.log("API response startRecording:", data);
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  React.useImperativeHandle(ref, () => ({
    stopRecording: () => stopRecording(roomName),
  }));

  const stopRecording = async (room) => {
    try {
      const response = await fetch(`${baseUrl}/api/stopRecording`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          room: room,
        }),
      });

      const data = await response.json();
      console.log("API response stopRecording:", data);
      return data;
    } catch (error) {
      console.error("Error stopping recording:", error);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-4 text-center">
        <h4>Loading monitoring interface....</h4>
        <div className="spinner-border text-primary mt-3" role="status"></div>
      </div>
    );
  }

  const sortedParticipants = [...participants].sort((a, b) => {
    const roleA = a.decodedIdentity?.role?.toLowerCase() || "";
    const roleB = b.decodedIdentity?.role?.toLowerCase() || "";
    if (roleA === "assessor" && roleB !== "assessor") return -1;
    if (roleA !== "assessor" && roleB === "assessor") return 1;
    return 0;
  });

  return (
    <div className="container-fluid py-4">
      <div className="text-center mb-4">
        {trainee ? (
          <>
            <h4> Practical Exam for trainee {trainee.Username} of batch </h4>
            <p>{user?.BatchId || ""}</p>
          </>
        ) : (
          <h4>Practical Exam</h4>
        )}
      </div>

      {participants.length === 0 && connected && (
        <>
          {trainee ? (
            <div className="alert alert-warning text-center">
              Waiting for the trainee to join the practical exam...
            </div>
          ) : (
            <div className="alert alert-warning text-center">
              Waiting for the assessor to join the practical exam...
            </div>
          )}
        </>
      )}

      <div className="row mt-3 g-3">
        {sortedParticipants.map((p) => (
          <div key={p.uniqueKey} className="col-12">
            <ParticipantVideo
              participantData={p}
              baseUrl={baseUrl}
              authToken={authToken}
              roomName={roomName}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

const ParticipantVideo = ({
  participantData,
  baseUrl,
  authToken,
  roomName,
}) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isMicMuted, setIsMicMuted] = useState(false);

  const participant = participantData.participant;
  const identity = participantData.decodedIdentity;
  const isLocal = participantData.isLocal;

  useEffect(() => {
    if (!participant) return;

    const videoElement = videoRef.current;
    const audioElement = audioRef.current;

    const attachTracks = async () => {
      const videoPublication = Array.from(
        participant.videoTrackPublications.values()
      ).find((pub) => pub.track && pub.kind === Track.Kind.Video);

      if (videoPublication?.track && videoElement) {
        videoPublication.track.attach(videoElement);
      }

      if (!isLocal) {
        const audioPublication = Array.from(
          participant.audioTrackPublications.values()
        ).find((pub) => pub.track && pub.kind === Track.Kind.Audio);

        if (audioPublication?.track && audioElement) {
          audioPublication.track.attach(audioElement);
          audioElement
            .play()
            .catch((err) => console.warn("Audio play blocked:", err));
          setIsMicMuted(audioPublication.isMuted);
        }
      }
    };

    const detachTracks = () => {
      const videoPublication = Array.from(
        participant.videoTrackPublications.values()
      ).find((pub) => pub.track && pub.kind === Track.Kind.Video);
      if (videoPublication?.track && videoElement) {
        videoPublication.track.detach(videoElement);
      }

      if (!isLocal) {
        const audioPublication = Array.from(
          participant.audioTrackPublications.values()
        ).find((pub) => pub.track && pub.kind === Track.Kind.Audio);
        if (audioPublication?.track && audioElement) {
          audioPublication.track.detach(audioElement);
        }
      }
    };

    attachTracks();

    participant.on(RoomEvent.TrackPublished, attachTracks);
    participant.on(RoomEvent.TrackSubscribed, attachTracks);
    participant.on(RoomEvent.TrackUnsubscribed, detachTracks);
    participant.on(RoomEvent.TrackMuted, (publication) => {
      if (publication.kind === Track.Kind.Audio) setIsMicMuted(true);
    });
    participant.on(RoomEvent.TrackUnmuted, (publication) => {
      if (publication.kind === Track.Kind.Audio) setIsMicMuted(false);
    });

    return () => {
      participant.off(RoomEvent.TrackPublished, attachTracks);
      participant.off(RoomEvent.TrackSubscribed, attachTracks);
      participant.off(RoomEvent.TrackUnsubscribed, detachTracks);
      detachTracks();
    };
  }, [participant, isLocal]);

  const callMuteAPI = async (roomName, participantSid, trackSid, mute) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${authToken}`);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({
          roomName: roomName,
          participantSid: participantSid,
          trackSid: trackSid,
          mute: mute,
        }),
      };

      const response = await fetch(
        `${baseUrl}/api/muteUnmuteParticipant`,
        requestOptions
      );
      const result = await response.json();
      console.log("Mute/Unmute API response:", result);
    } catch (error) {
      console.error("Error calling mute/unmute API:", error);
    }
  };

  const toggleMicrophone = async () => {
    console.log("participant", participant);
    const firstKey = participant.audioTrackPublications.keys().next().value;

    console.log("participant-audioTrackPublications", firstKey);

    if (isLocal) {
      const newState = !isMicMuted;
      await participant.setMicrophoneEnabled(!newState);
      setIsMicMuted(newState);

      await callMuteAPI(
        roomName,
        participant.participantInfo?.identity,
        firstKey,
        newState
      );
    } else {
      const newState = !isMicMuted;
      setIsMicMuted(newState);
      await callMuteAPI(
        roomName,
        participant.participantInfo?.identity,
        firstKey,
        newState
      );
    }
  };

  const isAssessor = identity?.role?.toLowerCase() === "assessor";

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%",
        background: "#1a1a1a",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: isAssessor
          ? "0 4px 20px rgba(34, 197, 94, 0.4)"
          : "0 4px 12px rgba(0,0,0,0.15)",
        border: isAssessor ? "2px solid #22c55e" : "none",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <audio ref={audioRef} autoPlay />

      {/* Control Buttons */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          display: "flex",
          gap: "8px",
        }}
      >
        <button
          onClick={toggleMicrophone}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            background: isMicMuted ? "#ef4444" : "rgba(255,255,255,0.2)",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            backdropFilter: "blur(10px)",
          }}
          title={isMicMuted ? "Unmute Microphone" : "Mute Microphone"}
        >
          {isMicMuted ? "🔇" : "🎤"}
        </button>
      </div>

      {/* Info Overlay */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.9), transparent)",
          color: "#fff",
          padding: "16px",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "2px" }}>
          {identity?.username || "N/A"} {isLocal && "(You)"}
        </div>
        <div
          style={{
            fontSize: "12px",
            opacity: 0.8,
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <span>Role:</span>
          <span
            style={{
              background: "rgba(34, 197, 94, 0.3)",
              color: "#22c55e",
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: "700",
              textTransform: "uppercase",
            }}
          >
            {identity?.role || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PracticalWEBRTCMonitoring;
